/**
 * Making the sign-in form work with password managers.
 *
 * The form Amplify renders is, on inspection, exactly right: a real <form method=post>,
 * `name="username"` with `autocomplete="username"`, `name="password"` with
 * `autocomplete="current-password"`, and a `type="submit"` button. Autofill still did
 * not work, and neither did RoboForm, for two reasons that have nothing to do with the
 * markup.
 *
 * ONE: NOTHING WAS EVER SAVED
 * ---------------------------
 * Signing in here is an XHR. Amplify does SRP against Cognito and then swaps the React
 * tree; the page never navigates. A browser's "save this password?" prompt is driven
 * largely by a form submission followed by a navigation, so on an app like this it
 * frequently never appears - and a password manager with nothing stored has nothing to
 * autofill. The symptom reads as "autofill is broken" when the real state is "there is
 * no saved credential to fill".
 *
 * navigator.credentials.store() is the API that exists for exactly this: it tells the
 * browser, explicitly, that a sign-in succeeded and with what. The browser still asks
 * the person before saving anything - this cannot store a credential behind their back.
 *
 * TWO: THE FIELD IDS WERE NOT VALID SELECTORS
 * -------------------------------------------
 * Amplify ids its inputs from React's useId, which produces `amplify-id-:r1:`. The
 * colons are legal in an HTML id and illegal in a CSS id selector, so
 * `document.querySelector('#amplify-id-:r1:')` throws SyntaxError - verified on the
 * deployed page. A password manager that remembers "the username field on this site"
 * by building a selector from the id therefore fails outright here, which is the more
 * likely explanation for RoboForm specifically.
 *
 * They are also UNSTABLE: useId numbers by position in the render tree, so any rule
 * keyed on `:r1:` stops matching the next time the tree changes shape.
 *
 * Amplify's formFields API has no `id` option (see FormFieldOptions in
 * @aws-amplify/ui), so the ids are rewritten here after render. Only the field's own
 * `<label for>` references them - checked on the live page rather than assumed - so
 * this is a two-attribute rename and not a reach into somebody else's component tree.
 */

/** Stable, selector-safe ids. Plain ASCII and hyphens, so `#id` parses. */
const FIELD_IDS: Record<string, string> = {
  username: 'planning-signin-username',
  password: 'planning-signin-password',
};

/**
 * Give the sign-in inputs ids a password manager can match on, and keep them.
 *
 * Returns a teardown. The MutationObserver is not belt-and-braces: Amplify re-renders
 * this form on every step change - a wrong password, an MFA prompt, the
 * new-password screen - and each render writes React's own id back over ours.
 */
export function stabiliseFieldIds(root: HTMLElement): () => void {
  const apply = () => {
    for (const [name, id] of Object.entries(FIELD_IDS)) {
      const input = root.querySelector<HTMLInputElement>(`input[name="${name}"]`);
      if (!input || input.id === id) {
        continue;
      }
      // The label first, while it can still be found by the old id. Reversing these
      // two lines silently unlabels the field, which a screen reader notices and a
      // sighted reviewer does not.
      const label = input.id
        ? root.querySelector<HTMLLabelElement>(`label[for="${CSS.escape(input.id)}"]`)
        : null;
      input.id = id;
      if (label) {
        label.htmlFor = id;
      }
    }
  };

  apply();
  const observer = new MutationObserver(apply);
  observer.observe(root, { childList: true, subtree: true });
  return () => observer.disconnect();
}

/**
 * Remember what was typed into the sign-in form, so it can be offered to the browser
 * once the sign-in actually succeeds.
 *
 * Captured on submit because that is the only moment both fields are populated: by the
 * time authStatus flips, Amplify has unmounted the form. Held in a closure, never in
 * React state - state is inspectable in devtools and survives in a component tree, and
 * this is a password.
 *
 * `capture: true` so it runs before Amplify's own handler, which is free to do what it
 * likes with the event afterwards.
 */
export function captureCredential(root: HTMLElement): {
  taken: () => { id: string; password: string } | null;
  stop: () => void;
} {
  let held: { id: string; password: string } | null = null;

  const onSubmit = (event: Event) => {
    const form = (event.target as HTMLElement | null)?.closest?.('form');
    if (!form) {
      return;
    }
    const id = form.querySelector<HTMLInputElement>('input[name="username"]')?.value ?? '';
    const password =
      form.querySelector<HTMLInputElement>('input[name="password"]')?.value ?? '';
    held = id && password ? { id, password } : null;
  };

  root.addEventListener('submit', onSubmit, true);
  return {
    // Reading it CLEARS it. A failed sign-in must not leave the last attempt sitting
    // in memory waiting for some later success to offer it to the browser.
    taken: () => {
      const value = held;
      held = null;
      return value;
    },
    stop: () => {
      held = null;
      root.removeEventListener('submit', onSubmit, true);
    },
  };
}

/**
 * Offer a just-used credential to the browser's password manager.
 *
 * Silent about everything. A browser without the API, a person who declines the
 * prompt, a policy that blocks it - none of those are failures worth a message on a
 * screen somebody has just successfully signed in to. Nothing here is logged, because
 * the thing it would be logging is a password.
 */
export async function rememberCredential(
  credential: { id: string; password: string } | null
): Promise<void> {
  /*
    Typed here rather than pulled from lib.dom. PasswordCredential is in neither the
    TypeScript DOM library nor Safari, so the declaration and the feature detection are
    the same line of defence: if it is absent this does nothing, and the compiler has
    no opinion about a global it cannot see.
  */
  const Ctor = (
    window as unknown as {
      PasswordCredential?: new (data: { id: string; password: string }) => Credential;
    }
  ).PasswordCredential;
  if (!credential || !Ctor || !window.isSecureContext || !navigator.credentials) {
    return;
  }
  try {
    await navigator.credentials.store(new Ctor(credential));
  } catch {
    // Declined, blocked, or unsupported in practice. The person is signed in either
    // way, which is the only thing this screen owes them.
  }
}
